import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";

/**
 * Deluxxie · Launchpad hub — bold editorial "four doors" homepage.
 * Anton display + Space Grotesk body, cream #F2EFE9 canvas with grain,
 * dark doors with accent numbers. The Solo & Reels are intentionally hidden.
 */
export default function StorefrontHub() {
  const [counts, setCounts] = useState({ drop: null, pre: null, stock: null });
  const [cut, setCut] = useState({ live: false, label: "Opens soon" });
  const navigate = useNavigate();

  useEffect(() => {
    api.publicListCampaigns().then((camps) => {
      setCounts({
        drop: camps.filter((c) => !c.roundId && c.status === "voting").length,
        pre: camps.filter((c) => c.status === "pre_order").length,
        stock: camps.filter((c) => c.status === "launched").length,
      });
    }).catch(() => {});
    api.cutActiveRound().then(async (r) => {
      if (r && r.status === "active") {
        const lb = await api.cutGetLeaderboard(r.id).catch(() => ({ brands: [] }));
        const b = lb.brands || [];
        let label;
        if (b.length >= 2) {
          const gap = (b[0].totalVotes || 0) - (b[1].totalVotes || 0);
          label = gap > 0 ? `${b[0].brandName} leads by ${gap}` : `${b[0].brandName} tied · live`;
        } else if (b.length === 1) {
          label = `${b[0].brandName} · ${b[0].totalVotes || 0} votes`;
        } else {
          label = "Voting live";
        }
        setCut({ live: true, label });
      } else {
        setCut({ live: false, label: "Opens soon" });
      }
    }).catch(() => {});
  }, []);

  return (
    <div className="dc-hub grain" data-testid="storefront-hub">
      <style>{CSS}</style>

      <nav className="dc-nav">
        <a href="#top" className="display dc-logo" data-testid="hub-logo">Deluxxie</a>
        <div className="dc-nav-links">
          <a href="https://deluxxie.com/account">Account</a>
          <a href="https://deluxxie.com/cart">Cart</a>
        </div>
      </nav>

      <div className="dc-head">
        <h1 className="display dc-title">Launchpad</h1>
        <p className="dc-sub">Three rooms. Vote on what gets made, back what won, or shop what's already here.</p>
      </div>

      <div className="dc-doors">
        <Door n="01" testId="hub-door-drop" onClick={() => navigate("/storefront/launches")}
          accent="#C8A832" tapeBg="#C8A832" tapeFg="#0E0D0C"
          tape={`${counts.drop ?? "—"} live`} title="Drop Room"
          desc="Vote to shop. Decide what deserves to exist." cta="Vote now →" />

        <Door n="02" testId="hub-door-cut" onClick={() => navigate("/the-cut")}
          accent="#5B4FE0" tapeBg="#5B4FE0" tapeFg="#F2EFE9"
          tape={cut.label} title="The Cut"
          desc="Brands go head-to-head. Hit the votes, make the cut." cta="Back a brand →" />

        <Door n="03" testId="hub-door-preorder" onClick={() => navigate("/storefront/preorders")}
          accent="#F2EFE9" tapeBg="#F2EFE9" tapeFg="#0E0D0C"
          tape={`${counts.pre ?? "—"} open`} title="Pre-Order Now"
          desc="Winners hitting threshold. Back it, get it first." cta="Back a winner →" />

        <Door n="04" testId="hub-door-stock" onClick={() => navigate("/storefront/just-dropped")}
          light accent="#0E0D0C" tapeBg="#0E0D0C" tapeFg="#F2EFE9"
          tape={`${counts.stock ?? "—"} in stock`} title="Just Dropped"
          desc="In stock, ships now. Made the cut or just landed." cta="Shop now →" />
      </div>
    </div>
  );
}

const Door = ({ n, tape, tapeBg, tapeFg, title, desc, cta, accent, light, onClick, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`door ${light ? "door-light" : ""}`}>
    <div className="door-top">
      <div className="display door-num" style={{ color: accent }}>{n}</div>
      <div className="tape door-tape" style={{ background: tapeBg, color: tapeFg }}>{tape}</div>
    </div>
    <div className="door-body">
      <div className="display door-title" style={{ color: light ? "#0E0D0C" : accent }}>{title}</div>
      <div className="door-desc" style={{ color: light ? "#3A3733" : "#C9C3B8" }}>{desc}</div>
      <div className="tape arrow door-cta" style={{ color: light ? "#0E0D0C" : accent }}>{cta}</div>
    </div>
  </button>
);

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Anton&family=Space+Grotesk:wght@400;500;700&display=swap');
.dc-hub{min-height:100vh;background:#F2EFE9;color:#0E0D0C;font-family:'Space Grotesk','Helvetica Neue',Arial,sans-serif;display:flex;flex-direction:column}
.dc-hub .display{font-family:'Anton','Impact','Arial Narrow',sans-serif;text-transform:uppercase;letter-spacing:.01em;line-height:.92}
.dc-hub .tape{font-family:'Anton',sans-serif;text-transform:uppercase;letter-spacing:.08em}
.grain{background-image:repeating-linear-gradient(0deg,rgba(0,0,0,0.03) 0px,rgba(0,0,0,0.03) 1px,transparent 1px,transparent 3px)}
.dc-nav{height:72px;box-sizing:border-box;padding:0 24px;display:flex;align-items:center;justify-content:space-between;border-bottom:2px solid #0E0D0C}
@media(min-width:768px){.dc-nav{padding:0 64px;height:80px}}
.dc-logo{font-size:26px}
@media(min-width:768px){.dc-logo{font-size:32px}}
.dc-nav-links{display:flex;gap:20px;font-size:13px;text-transform:uppercase;letter-spacing:.08em}
.dc-nav-links a:hover{opacity:.6}
.dc-head{padding:40px 24px 24px;display:flex;flex-direction:column;gap:16px;align-items:flex-start}
@media(min-width:900px){.dc-head{padding:56px 64px 32px;flex-direction:row;align-items:flex-end;justify-content:space-between}}
.dc-title{margin:0;font-size:56px}
@media(min-width:768px){.dc-title{font-size:96px}}
.dc-sub{margin:0;max-width:380px;font-size:16px;line-height:1.4;color:#3A3733}
@media(min-width:900px){.dc-sub{text-align:right;font-size:17px}}
.dc-doors{flex-grow:1;padding:0 24px 40px;display:grid;grid-template-columns:1fr;gap:20px}
@media(min-width:768px){.dc-doors{padding:0 64px 64px;grid-template-columns:repeat(2,minmax(0,1fr));gap:24px}}
.door{width:100%;text-align:left;min-height:220px;display:flex;flex-direction:column;justify-content:space-between;padding:32px;box-sizing:border-box;border:2px solid #FFFFFF;background:#141210;color:#F2EFE9;box-shadow:0 0 0 1px #0E0D0C;cursor:pointer;transition:background 120ms,transform 120ms;font-family:inherit}
@media(min-width:768px){.door{padding:40px;min-height:240px}}
.door:hover{background:#1C1A18;transform:translateY(-2px)}
.door-light{background:#FFFFFF;color:#0E0D0C;border-color:#0E0D0C;box-shadow:none}
.door-light:hover{background:#F2EFE9}
.door-top{display:flex;justify-content:space-between;align-items:flex-start}
.door-num{font-size:56px}
@media(min-width:768px){.door-num{font-size:72px}}
.door-tape{padding:6px 12px;font-size:12px}
.door-body{display:flex;flex-direction:column;gap:10px}
.door-title{font-size:40px}
@media(min-width:768px){.door-title{font-size:56px}}
.door-desc{font-size:16px;line-height:1.35}
.door-cta{font-size:17px;margin-top:6px;transition:transform 120ms}
.door:hover .door-cta{transform:translateX(8px)}
`;
